import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Wallet, Plus, Settings, CalendarDays, Check, Edit, Trash2, 
  RefreshCw, Home, Shield, Droplets, Zap, Wifi, Car, Wrench, Users, ShoppingCart
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = {
  loyer: { label: 'Loyer', icon: Home, color: 'bg-blue-100 text-blue-800' },
  securite: { label: 'Sécurité', icon: Shield, color: 'bg-purple-100 text-purple-800' },
  eau: { label: 'Eau', icon: Droplets, color: 'bg-cyan-100 text-cyan-800' },
  electricite: { label: 'Électricité', icon: Zap, color: 'bg-yellow-100 text-yellow-800' },
  internet: { label: 'Internet', icon: Wifi, color: 'bg-green-100 text-green-800' },
  assurance: { label: 'Assurance', icon: Shield, color: 'bg-indigo-100 text-indigo-800' },
  carburant: { label: 'Carburant', icon: Car, color: 'bg-orange-100 text-orange-800' },
  maintenance: { label: 'Maintenance', icon: Wrench, color: 'bg-gray-100 text-gray-800' },
  salaire: { label: 'Salaire', icon: Users, color: 'bg-pink-100 text-pink-800' },
  achat_materiaux: { label: 'Achat Matériaux', icon: ShoppingCart, color: 'bg-amber-100 text-amber-800' },
  autre: { label: 'Autre', icon: Wallet, color: 'bg-slate-100 text-slate-800' }
};

const MONTHS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

const ExpensesPage = () => {
  const { getAuthHeaders } = useAuth();
  const [activeTab, setActiveTab] = useState('monthly');
  const [loading, setLoading] = useState(true);
  
  // Fixed expenses state
  const [fixedExpenses, setFixedExpenses] = useState([]);
  const [showFixedModal, setShowFixedModal] = useState(false);
  const [editingFixed, setEditingFixed] = useState(null);
  const [fixedForm, setFixedForm] = useState({ name: '', category: 'loyer', amount: '' });
  
  // Monthly expenses state
  const [expenses, setExpenses] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [expenseForm, setExpenseForm] = useState({ category: 'carburant', description: '', amount: '' });
  
  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteType, setDeleteType] = useState(null);

  useEffect(() => {
    fetchAll();
  }, []);

  useEffect(() => {
    fetchExpenses();
  }, [selectedMonth, selectedYear]);

  const fetchAll = async () => {
    setLoading(true);
    await Promise.all([fetchFixedExpenses(), fetchExpenses()]);
    setLoading(false);
  };

  const fetchFixedExpenses = async () => {
    try {
      const response = await axios.get(`${API}/fixed-expenses`, getAuthHeaders());
      setFixedExpenses(response.data);
    } catch (error) {
      console.error('Error fetching fixed expenses:', error);
    }
  };

  const fetchExpenses = async () => {
    try {
      const response = await axios.get(
        `${API}/expenses?month=${selectedMonth}&year=${selectedYear}`,
        getAuthHeaders()
      );
      setExpenses(response.data);
    } catch (error) {
      console.error('Error fetching expenses:', error);
    }
  };

  // Fixed expenses handlers
  const handleSaveFixed = async () => {
    if (!fixedForm.name || !fixedForm.amount) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    try {
      if (editingFixed) {
        await axios.put(
          `${API}/fixed-expenses/${editingFixed.id}`,
          { name: fixedForm.name, amount: parseFloat(fixedForm.amount) },
          getAuthHeaders()
        );
        toast.success('Dépense fixe modifiée');
      } else {
        await axios.post(
          `${API}/fixed-expenses`,
          { ...fixedForm, amount: parseFloat(fixedForm.amount) },
          getAuthHeaders()
        );
        toast.success('Dépense fixe ajoutée');
      }
      setShowFixedModal(false);
      setEditingFixed(null);
      setFixedForm({ name: '', category: 'loyer', amount: '' });
      fetchFixedExpenses();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const openEditFixed = (expense) => {
    setEditingFixed(expense);
    setFixedForm({ name: expense.name, category: expense.category, amount: expense.amount.toString() });
    setShowFixedModal(true);
  };

  const handleDeleteFixed = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/fixed-expenses/${deleteTarget.id}`, getAuthHeaders());
      toast.success('Dépense fixe supprimée');
      setDeleteTarget(null);
      setDeleteType(null);
      fetchFixedExpenses();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Generate monthly expenses
  const handleGenerateMonthly = async () => {
    try {
      const response = await axios.post(
        `${API}/expenses/generate-monthly?month=${selectedMonth}&year=${selectedYear}`,
        {},
        getAuthHeaders()
      );
      toast.success(response.data.message);
      fetchExpenses();
    } catch (error) {
      toast.error('Erreur lors de la génération');
    }
  };

  // Monthly expense handlers
  const handleSaveExpense = async () => {
    if (!expenseForm.description || !expenseForm.amount) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    try {
      await axios.post(
        `${API}/expenses`,
        {
          ...expenseForm,
          amount: parseFloat(expenseForm.amount),
          month: selectedMonth,
          year: selectedYear
        },
        getAuthHeaders()
      );
      toast.success('Dépense ajoutée');
      setShowExpenseModal(false);
      setExpenseForm({ category: 'carburant', description: '', amount: '' });
      fetchExpenses();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleTogglePaid = async (expense) => {
    try {
      await axios.put(
        `${API}/expenses/${expense.id}`,
        { is_paid: !expense.is_paid, paid_date: !expense.is_paid ? new Date().toISOString() : null },
        getAuthHeaders()
      );
      fetchExpenses();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteExpense = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/expenses/${deleteTarget.id}`, getAuthHeaders());
      toast.success('Dépense supprimée');
      setDeleteTarget(null);
      setDeleteType(null);
      fetchExpenses();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Stats
  const totalFixed = fixedExpenses.filter(e => e.is_active).reduce((sum, e) => sum + e.amount, 0);
  const totalMonthly = expenses.reduce((sum, e) => sum + e.amount, 0);
  const totalPaid = expenses.filter(e => e.is_paid).reduce((sum, e) => sum + e.amount, 0);
  const totalUnpaid = totalMonthly - totalPaid;

  const getCategoryInfo = (cat) => CATEGORIES[cat] || CATEGORIES.autre;

  if (loading) {
    return <div className="flex items-center justify-center h-full">Chargement...</div>;
  }

  return (
    <div className="space-y-6" data-testid="expenses-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold text-[#1B3268] tracking-tight flex items-center gap-3">
            <Wallet className="w-10 h-10" />
            Gestion des Dépenses
          </h1>
          <p className="text-sm text-slate-600 mt-1">Dépenses fixes mensuelles et opérationnelles</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-sm">
          <div className="flex items-center gap-3">
            <Settings className="w-8 h-8" />
            <div>
              <div className="text-sm text-white/70">Charges Fixes/Mois</div>
              <div className="text-2xl font-bold">{totalFixed.toLocaleString('fr-FR')} FCFA</div>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-sm">
          <div className="flex items-center gap-3">
            <CalendarDays className="w-8 h-8" />
            <div>
              <div className="text-sm text-white/70">Total {MONTHS[selectedMonth-1]}</div>
              <div className="text-2xl font-bold">{totalMonthly.toLocaleString('fr-FR')} FCFA</div>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-sm">
          <div className="flex items-center gap-3">
            <Check className="w-8 h-8" />
            <div>
              <div className="text-sm text-white/70">Payées</div>
              <div className="text-2xl font-bold">{totalPaid.toLocaleString('fr-FR')} FCFA</div>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-sm">
          <div className="flex items-center gap-3">
            <Wallet className="w-8 h-8" />
            <div>
              <div className="text-sm text-white/70">Non Payées</div>
              <div className="text-2xl font-bold">{totalUnpaid.toLocaleString('fr-FR')} FCFA</div>
            </div>
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-4">
          <TabsTrigger value="monthly" className="flex items-center gap-2">
            <CalendarDays className="w-4 h-4" />
            Dépenses du Mois
          </TabsTrigger>
          <TabsTrigger value="fixed" className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            Dépenses Fixes (Config)
          </TabsTrigger>
        </TabsList>

        {/* Monthly Expenses Tab */}
        <TabsContent value="monthly">
          {/* Month Selector & Actions */}
          <Card className="p-4 mb-4 bg-white border border-slate-200 rounded-sm">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Label>Mois:</Label>
                <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                  <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((m, i) => (
                      <SelectItem key={i+1} value={(i+1).toString()}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Label>Année:</Label>
                <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                  <SelectTrigger className="w-[100px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026, 2027].map(y => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={handleGenerateMonthly}
                variant="outline"
                className="border-[#1B3268] text-[#1B3268]"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Générer Charges Fixes
              </Button>
              <Button
                onClick={() => setShowExpenseModal(true)}
                className="bg-[#1B3268] hover:bg-[#152a55] text-white ml-auto"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle Dépense
              </Button>
            </div>
          </Card>

          {/* Expenses Table */}
          <Card className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-100 text-slate-700 font-semibold uppercase text-xs tracking-wider">
                    <th className="p-4 text-left">Catégorie</th>
                    <th className="p-4 text-left">Description</th>
                    <th className="p-4 text-right">Montant</th>
                    <th className="p-4 text-center">Type</th>
                    <th className="p-4 text-center">Statut</th>
                    <th className="p-4 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {expenses.map((expense) => {
                    const catInfo = getCategoryInfo(expense.category);
                    const Icon = catInfo.icon;
                    return (
                      <tr key={expense.id} className="hover:bg-slate-50 transition-colors border-b last:border-0">
                        <td className="p-4">
                          <span className={`inline-flex items-center gap-2 px-2 py-1 text-xs font-medium rounded-sm ${catInfo.color}`}>
                            <Icon className="w-3 h-3" />
                            {catInfo.label}
                          </span>
                        </td>
                        <td className="p-4">{expense.description}</td>
                        <td className="p-4 text-right font-mono font-bold text-red-600">
                          {expense.amount.toLocaleString('fr-FR')} FCFA
                        </td>
                        <td className="p-4 text-center">
                          {expense.is_fixed ? (
                            <Badge className="bg-blue-100 text-blue-800">Fixe</Badge>
                          ) : (
                            <Badge className="bg-gray-100 text-gray-800">Variable</Badge>
                          )}
                        </td>
                        <td className="p-4 text-center">
                          {expense.is_paid ? (
                            <Badge className="bg-green-100 text-green-800">Payée</Badge>
                          ) : (
                            <Badge className="bg-orange-100 text-orange-800">Non payée</Badge>
                          )}
                        </td>
                        <td className="p-4">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleTogglePaid(expense)}
                              title={expense.is_paid ? "Marquer non payée" : "Marquer payée"}
                              className={expense.is_paid ? "text-orange-600" : "text-green-600"}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setDeleteTarget(expense); setDeleteType('expense'); }}
                              className="text-red-500"
                              data-testid={`delete-expense-${expense.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {expenses.length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  <p>Aucune dépense pour {MONTHS[selectedMonth-1]} {selectedYear}</p>
                  <p className="text-sm mt-2">Cliquez sur "Générer Charges Fixes" pour créer les dépenses fixes du mois</p>
                </div>
              )}
            </div>
          </Card>
        </TabsContent>

        {/* Fixed Expenses Tab */}
        <TabsContent value="fixed">
          <Card className="p-4 mb-4 bg-white border border-slate-200 rounded-sm">
            <div className="flex items-center justify-between">
              <p className="text-slate-600">
                Configurez ici vos dépenses fixes mensuelles. Elles seront générées automatiquement chaque mois.
              </p>
              <Button
                onClick={() => { setEditingFixed(null); setFixedForm({ name: '', category: 'loyer', amount: '' }); setShowFixedModal(true); }}
                className="bg-[#1B3268] hover:bg-[#152a55] text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Ajouter Charge Fixe
              </Button>
            </div>
          </Card>

          {/* Fixed Expenses Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {fixedExpenses.map((expense) => {
              const catInfo = getCategoryInfo(expense.category);
              const Icon = catInfo.icon;
              return (
                <Card key={expense.id} className={`p-4 border rounded-sm ${expense.is_active ? 'bg-white' : 'bg-gray-100 opacity-60'}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-sm ${catInfo.color}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#1B3268]">{expense.name}</h3>
                        <p className="text-xs text-slate-500">{catInfo.label}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEditFixed(expense)} className="text-blue-600">
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => { setDeleteTarget(expense); setDeleteType('fixed'); }} className="text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t">
                    <p className="text-2xl font-bold text-[#1B3268]">{expense.amount.toLocaleString('fr-FR')} FCFA</p>
                    <p className="text-xs text-slate-500">par mois</p>
                  </div>
                </Card>
              );
            })}
            {fixedExpenses.length === 0 && (
              <div className="col-span-full text-center py-12 text-slate-500">
                <Settings className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Aucune charge fixe configurée</p>
                <p className="text-sm mt-2">Ajoutez vos charges fixes (loyer, eau, électricité...)</p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Fixed Expense Modal */}
      <Dialog open={showFixedModal} onOpenChange={setShowFixedModal}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="text-[#1B3268]">
              {editingFixed ? 'Modifier la charge fixe' : 'Nouvelle charge fixe'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nom</Label>
              <Input
                placeholder="Ex: Loyer local principal"
                value={fixedForm.name}
                onChange={(e) => setFixedForm({...fixedForm, name: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Catégorie</Label>
              <Select value={fixedForm.category} onValueChange={(v) => setFixedForm({...fixedForm, category: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="loyer">Loyer</SelectItem>
                  <SelectItem value="securite">Sécurité</SelectItem>
                  <SelectItem value="eau">Eau</SelectItem>
                  <SelectItem value="electricite">Électricité</SelectItem>
                  <SelectItem value="internet">Internet</SelectItem>
                  <SelectItem value="assurance">Assurance</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Montant mensuel (FCFA)</Label>
              <Input
                type="number"
                placeholder="0"
                value={fixedForm.amount}
                onChange={(e) => setFixedForm({...fixedForm, amount: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFixedModal(false)}>Annuler</Button>
            <Button onClick={handleSaveFixed} className="bg-[#1B3268] hover:bg-[#152a55] text-white">
              {editingFixed ? 'Modifier' : 'Ajouter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Expense Modal */}
      <Dialog open={showExpenseModal} onOpenChange={setShowExpenseModal}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="text-[#1B3268]">Nouvelle dépense variable</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Catégorie</Label>
              <Select value={expenseForm.category} onValueChange={(v) => setExpenseForm({...expenseForm, category: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="carburant">Carburant</SelectItem>
                  <SelectItem value="maintenance">Maintenance</SelectItem>
                  <SelectItem value="salaire">Salaire</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                placeholder="Ex: Achat gasoil générateur"
                value={expenseForm.description}
                onChange={(e) => setExpenseForm({...expenseForm, description: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Montant (FCFA)</Label>
              <Input
                type="number"
                placeholder="0"
                value={expenseForm.amount}
                onChange={(e) => setExpenseForm({...expenseForm, amount: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExpenseModal(false)}>Annuler</Button>
            <Button onClick={handleSaveExpense} className="bg-[#1B3268] hover:bg-[#152a55] text-white">
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => { setDeleteTarget(null); setDeleteType(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">Supprimer ?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteType === 'fixed' 
                ? `Voulez-vous supprimer la charge fixe "${deleteTarget?.name}" ?`
                : `Voulez-vous supprimer cette dépense ?`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={deleteType === 'fixed' ? handleDeleteFixed : handleDeleteExpense}
              className="bg-red-600 hover:bg-red-700"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ExpensesPage;
